<template>
  <div>
    <HrPageLayout
      title="Recruitment"
      subtitle="Vacancies, applications, interviews, selection and offer letters"
      icon="👥"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Staff Establishment' },
        { label: 'Recruitment' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-secondary" @click="refreshAll">
          <i class="bx bx-refresh"></i> Refresh
        </button>
        <button v-if="canVacancies" type="button" class="hr-btn hr-btn-primary" @click="openVacancyModal">
          <i class="bx bx-plus"></i> Create Vacancy
        </button>
      </template>

      <div class="recruitment-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-content">
              <p class="hr-metric-label">Open Vacancies</p>
            <h2 class="hr-metric-value">{{ metrics.openVacancies }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-content">
              <p class="hr-metric-label">Applications</p>
            <h2 class="hr-metric-value">{{ metrics.totalApplications }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-content">
              <p class="hr-metric-label">Shortlisted</p>
            <h2 class="hr-metric-value">{{ metrics.shortlisted }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-content">
              <p class="hr-metric-label">In Interview</p>
            <h2 class="hr-metric-value">{{ metrics.inInterview }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-content">
              <p class="hr-metric-label">Selected / Offered</p>
            <h2 class="hr-metric-value">{{ metrics.selected }}</h2>
            </div>
          </div>
        </div>

        <div class="recruitment-tabs hr-mb-3">
          <button
            v-for="t in visibleTabs"
            :key="t.id"
            type="button"
            class="recruitment-tab"
            :class="{ active: activeTab === t.id }"
            @click="activeTab = t.id"
          >
            <i :class="t.icon"></i> {{ t.label }}
          </button>
        </div>

        <!-- Vacancies -->
        <div v-show="activeTab === 'vacancies'" class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="recruitment-filters hr-mb-3">
              <select v-model="vacancyStatusFilter" class="hr-select" @change="loadVacancies">
                <option value="">All statuses</option>
                <option value="Draft">Draft</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Filled">Filled</option>
              </select>
            </div>
            <div v-if="loadingVacancies" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Title / Position</th>
                    <th>Department</th>
                    <th>Type</th>
                    <th>Closing</th>
                    <th>Applications</th>
                    <th>Status</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="v in vacancies" :key="v.id">
                    <td>
                      <strong>{{ v.title }}</strong>
                      <small class="hr-d-block hr-text-muted">{{ v.position_title }}</small>
                    </td>
                    <td>{{ v.department_name || '—' }}</td>
                    <td>{{ v.employment_type }}</td>
                    <td>{{ formatDate(v.closing_date) }}</td>
                    <td>{{ v.application_count || 0 }}</td>
                    <td><span class="hr-badge" :class="vacancyBadge(v.status)">{{ v.status }}</span></td>
                    <td class="hr-text-end">
                      <button v-if="v.status === 'Draft'" class="hr-btn hr-btn-sm hr-btn-success" @click="publishVacancy(v)">Publish</button>
                      <button class="hr-btn hr-btn-sm hr-btn-primary" @click="filterByVacancy(v)">Applications</button>
                    </td>
                  </tr>
                  <tr v-if="!vacancies.length">
                    <td colspan="7" class="hr-text-center hr-text-muted">No vacancies yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Applications -->
        <div v-show="activeTab === 'applications'" class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="recruitment-filters hr-mb-3">
              <select v-model="applicationVacancyFilter" class="hr-select" @change="loadApplications">
                <option :value="0">All vacancies</option>
                <option v-for="v in vacancies" :key="v.id" :value="v.id">{{ v.title }}</option>
              </select>
              <select v-model="applicationStatusFilter" class="hr-select" @change="loadApplications">
                <option value="">All statuses</option>
                <option value="Applied">Applied</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Interview">Interview</option>
                <option value="Selected">Selected</option>
                <option value="Offered">Offered</option>
                <option value="Rejected">Rejected</option>
              </select>
              <button v-if="canApplications" type="button" class="hr-btn hr-btn-primary" @click="openApplicationModal">
                <i class="bx bx-user-plus"></i> Add Application
              </button>
            </div>
            <div v-if="loadingApplications" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Vacancy</th>
                    <th>Contact</th>
                    <th>Applied</th>
                    <th>Status</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in applications" :key="a.id">
                    <td><strong>{{ a.applicant_name }}</strong></td>
                    <td>{{ a.vacancy_title || a.position_title }}</td>
                    <td>
                      <small>{{ a.email }}</small><br>
                      <small v-if="a.phone" class="hr-text-muted">{{ a.phone }}</small>
                    </td>
                    <td>{{ formatDateTime(a.applied_at) }}</td>
                    <td><span class="hr-badge" :class="appBadge(a.status)">{{ a.status }}</span></td>
                    <td class="hr-text-end">
                      <button v-if="canShortlist && a.status === 'Applied'" class="hr-btn hr-btn-sm hr-btn-success" @click="shortlistApplicant(a)">Shortlist</button>
                      <button v-if="canReject && !['Rejected','Offered','Withdrawn'].includes(a.status)" class="hr-btn hr-btn-sm hr-btn-danger" @click="rejectApplicant(a)">Reject</button>
                    </td>
                  </tr>
                  <tr v-if="!applications.length">
                    <td colspan="6" class="hr-text-center hr-text-muted">No applications found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Shortlisting -->
        <div v-show="activeTab === 'shortlisting'" class="hr-chart-card">
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">Candidates with status Applied — shortlist and send email notification.</p>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Position</th>
                    <th>Email</th>
                    <th class="hr-text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in applicationsForShortlist" :key="'s-' + a.id">
                    <td>{{ a.applicant_name }}</td>
                    <td>{{ a.position_title }}</td>
                    <td>{{ a.email }}</td>
                    <td class="hr-text-end">
                      <button class="hr-btn hr-btn-sm hr-btn-success" @click="shortlistApplicant(a)">
                        <i class="bx bx-envelope"></i> Shortlist &amp; Email
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!applicationsForShortlist.length">
                    <td colspan="4" class="hr-text-center hr-text-muted">No pending applications to shortlist.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Smart shortlist -->
        <div v-show="activeTab === 'smartShortlist'" class="hr-chart-card">
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">
              Score applicants against vacancy requirements and job description. Suggested candidates meet your match threshold.
            </p>
            <div class="recruitment-filters hr-mb-3">
              <select v-model="suggestVacancyId" class="hr-select" @change="loadSuggestedShortlist">
                <option :value="0">Select vacancy</option>
                <option v-for="v in vacancies" :key="'sg-' + v.id" :value="v.id">{{ v.title }}</option>
              </select>
              <label class="hr-form-label hr-mb-0">Threshold %</label>
              <input v-model.number="suggestedThreshold" type="number" min="0" max="100" class="hr-input" style="max-width:100px" @change="loadSuggestedShortlist">
              <button type="button" class="hr-btn hr-btn-secondary" :disabled="!suggestVacancyId || loadingSuggested" @click="scoreVacancyApplications">
                <i class="bx bx-calculator"></i> Re-score all
              </button>
              <button type="button" class="hr-btn hr-btn-primary" :disabled="!suggestVacancyId || loadingSuggested" @click="bulkShortlistSuggested">
                <i class="bx bx-check-double"></i> Shortlist suggested
              </button>
            </div>
            <div v-if="loadingSuggested" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Match %</th>
                    <th>Status</th>
                    <th>Suggested</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in suggestedApplications" :key="'sga-' + a.id">
                    <td>{{ a.applicant_name }}</td>
                    <td>
                      <strong :class="scoreClass(a.fit_score)">{{ formatScore(a.fit_score) }}</strong>
                      <small v-if="a.match_summary" class="hr-d-block hr-text-muted">{{ a.match_summary }}</small>
                    </td>
                    <td><span class="hr-badge" :class="appBadge(a.status)">{{ a.status }}</span></td>
                    <td>
                      <span v-if="a.suggested" class="hr-badge hr-badge-success">Yes</span>
                      <span v-else class="hr-badge hr-badge-secondary">No</span>
                    </td>
                    <td class="hr-text-end">
                      <button v-if="a.status === 'Applied'" class="hr-btn hr-btn-sm hr-btn-success" @click="shortlistApplicant(a)">Shortlist</button>
                    </td>
                  </tr>
                  <tr v-if="!suggestedApplications.length">
                    <td colspan="5" class="hr-text-center hr-text-muted">Select a vacancy to load scored applicants.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Interviews -->
        <div v-show="activeTab === 'interviews'" class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="recruitment-filters hr-mb-3">
              <button v-if="canInterviews" type="button" class="hr-btn hr-btn-primary" @click="openInterviewModal">
                <i class="bx bx-calendar-plus"></i> Schedule Interview
              </button>
            </div>
            <div v-if="loadingInterviews" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Position</th>
                    <th>When</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="i in interviews" :key="i.id">
                    <td>{{ i.applicant_name }}</td>
                    <td>{{ i.position_title }}</td>
                    <td>{{ formatDateTime(i.scheduled_at) }}</td>
                    <td>{{ i.interview_type }}</td>
                    <td><span class="hr-badge hr-badge-secondary">{{ i.status }}</span></td>
                    <td class="hr-text-end">
                      <button v-if="i.status === 'Scheduled'" class="hr-btn hr-btn-sm hr-btn-success" @click="completeInterview(i)">Complete</button>
                    </td>
                  </tr>
                  <tr v-if="!interviews.length">
                    <td colspan="6" class="hr-text-center hr-text-muted">No interviews scheduled.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Selection -->
        <div v-show="activeTab === 'selection'" class="hr-chart-card">
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">Shortlisted or interviewed candidates ready for final selection.</p>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th class="hr-text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in applicationsForSelection" :key="'sel-' + a.id">
                    <td>{{ a.applicant_name }}</td>
                    <td>{{ a.position_title }}</td>
                    <td><span class="hr-badge" :class="appBadge(a.status)">{{ a.status }}</span></td>
                    <td class="hr-text-end">
                      <button v-if="canSelection && a.status !== 'Selected'" class="hr-btn hr-btn-sm hr-btn-primary" @click="selectApplicant(a)">Select</button>
                    </td>
                  </tr>
                  <tr v-if="!applicationsForSelection.length">
                    <td colspan="4" class="hr-text-center hr-text-muted">No candidates ready for selection.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Offers -->
        <div v-show="activeTab === 'offers'" class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="recruitment-filters hr-mb-3">
              <button v-if="canOffers" type="button" class="hr-btn hr-btn-primary" @click="openOfferModal">
                <i class="bx bx-file"></i> Generate Offer
              </button>
            </div>
            <div v-if="loadingOffers" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Candidate</th>
                    <th>Position</th>
                    <th>Start</th>
                    <th>Status</th>
                    <th>Email</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="o in offers" :key="o.id">
                    <td>{{ o.offer_ref }}</td>
                    <td>{{ o.applicant_name }}</td>
                    <td>{{ o.position_title }}</td>
                    <td>{{ formatDate(o.start_date) }}</td>
                    <td><span class="hr-badge" :class="o.status === 'Sent' ? 'hr-badge-success' : 'hr-badge-secondary'">{{ o.status }}</span></td>
                    <td>{{ o.email_sent ? 'Sent' : '—' }}</td>
                    <td class="hr-text-end">
                      <button v-if="o.status === 'Draft'" class="hr-btn hr-btn-sm hr-btn-success" @click="sendOffer(o)">
                        <i class="bx bx-send"></i> Send &amp; Email
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-outline" @click="previewOffer(o)">Preview</button>
                    </td>
                  </tr>
                  <tr v-if="!offers.length">
                    <td colspan="7" class="hr-text-center hr-text-muted">No offer letters yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Onboarding & hire -->
        <div v-show="activeTab === 'onboarding'" class="hr-chart-card">
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-3">Send onboarding invites, review submitted forms, approve, and create employee records.</p>
            <div class="recruitment-filters hr-mb-3">
              <select v-model="onboardingStatusFilter" class="hr-select" @change="loadOnboardingSubmissions">
                <option value="Submitted">Submitted</option>
                <option value="Under Review">Under Review</option>
                <option value="Approved">Approved</option>
              </select>
              <button type="button" class="hr-btn hr-btn-secondary" @click="loadOnboardingSubmissions">
                <i class="bx bx-refresh"></i> Refresh reviews
              </button>
            </div>

            <h4 class="hr-mb-2">Selected / offered candidates</h4>
            <div class="hr-table-wrapper hr-mb-4">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in onboardingCandidates" :key="'onb-' + a.id">
                    <td>{{ a.applicant_name }}</td>
                    <td>{{ a.position_title }}</td>
                    <td><span class="hr-badge" :class="appBadge(a.status)">{{ a.status }}</span></td>
                    <td class="hr-text-end">
                      <button class="hr-btn hr-btn-sm hr-btn-primary" @click="sendOnboardingInvite(a)">Send onboarding</button>
                      <button class="hr-btn hr-btn-sm hr-btn-success" @click="hireApplicant(a)">Hire → employee</button>
                    </td>
                  </tr>
                  <tr v-if="!onboardingCandidates.length">
                    <td colspan="4" class="hr-text-center hr-text-muted">No selected or offered candidates.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 class="hr-mb-2">Form submissions for HR review</h4>
            <div v-if="loadingOnboarding" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Form</th>
                    <th>Applicant</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in onboardingSubmissions" :key="s.id">
                    <td>{{ s.template_name }}</td>
                    <td>{{ s.applicant_name || s.email }}</td>
                    <td><span class="hr-badge hr-badge-secondary">{{ s.status }}</span></td>
                    <td>{{ formatDateTime(s.submitted_at) }}</td>
                    <td class="hr-text-end">
                      <button v-if="['Submitted','Under Review'].includes(s.status)" class="hr-btn hr-btn-sm hr-btn-success" @click="approveSubmission(s)">Approve</button>
                      <button class="hr-btn hr-btn-sm hr-btn-outline" @click="downloadFormsPack(s)">Export</button>
                    </td>
                  </tr>
                  <tr v-if="!onboardingSubmissions.length">
                    <td colspan="5" class="hr-text-center hr-text-muted">No submissions in this status.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Automation & forms -->
        <div v-show="activeTab === 'settings'" class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="hr-form-grid">
              <div class="hr-filter-group span-full">
                <h4>Recruitment automation</h4>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Auto-score on application</label>
                <select v-model="automationSettings.recruitment_auto_score_on_apply" class="hr-select">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Shortlist suggestion threshold (%)</label>
                <input v-model="automationSettings.recruitment_shortlist_threshold" class="hr-input" type="number" min="0" max="100">
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Send automated emails</label>
                <select v-model="automationSettings.recruitment_auto_email" class="hr-select">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div class="hr-filter-group span-full">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="saveAutomationSettings">Save automation</button>
              </div>

              <div class="hr-filter-group span-full hr-mt-4">
                <h4>Employee number format</h4>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Mode</label>
                <select v-model="empNumberSettings.mode" class="hr-select">
                  <option value="auto">Auto-generate (prefix + sequence)</option>
                  <option value="manual">Manual entry at hire</option>
                  <option value="national_id">Use national ID as emp. number</option>
                </select>
              </div>
              <div v-if="empNumberSettings.mode === 'auto'" class="hr-filter-group span-half">
                <label class="hr-form-label">Prefix</label>
                <input v-model="empNumberSettings.prefix" class="hr-input" type="text">
              </div>
              <div v-if="empNumberSettings.mode === 'auto'" class="hr-filter-group span-half">
                <label class="hr-form-label">Pad length</label>
                <input v-model.number="empNumberSettings.pad_length" class="hr-input" type="number" min="3" max="10">
              </div>
              <div class="hr-filter-group span-half">
                <button type="button" class="hr-btn hr-btn-secondary" @click="previewNextEmpNo">Preview next number</button>
                <small v-if="previewEmpNo" class="hr-d-block hr-mt-1">Next: <strong>{{ previewEmpNo }}</strong></small>
              </div>
              <div class="hr-filter-group span-full">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="saveEmpNumberSettings">Save employee number settings</button>
              </div>

              <div class="hr-filter-group span-full hr-mt-4">
                <h4>Onboarding form templates</h4>
                <p class="hr-text-muted">Upload company forms (PDF/DOC). The system creates digital fields with prefill from applicant data.</p>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Template name</label>
                <input v-model="formTemplateUpload.name" class="hr-input" type="text" placeholder="e.g. Employment declaration">
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Approval</label>
                <select v-model="formTemplateUpload.approvalMode" class="hr-select">
                  <option value="sequential">Several positions (sequential)</option>
                  <option value="single">Single position only</option>
                </select>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Approver position</label>
                <select v-model="formTemplateUpload.approverPositionId" class="hr-select">
                  <option :value="0">— Optional —</option>
                  <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.positionName || p.name }}</option>
                </select>
              </div>
              <div class="hr-filter-group span-half">
                <label class="hr-form-label">Form file</label>
                <input ref="formFileInput" type="file" class="hr-input" accept=".pdf,.doc,.docx">
              </div>
              <div class="hr-filter-group span-full">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="uploadFormTemplate">Upload &amp; create form</button>
              </div>
            </div>

            <div class="hr-table-wrapper hr-mt-4">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Category</th>
                    <th>Fields</th>
                    <th>Approval</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in formTemplates" :key="t.id">
                    <td>{{ t.template_name }}</td>
                    <td>{{ t.category }}</td>
                    <td>{{ t.field_count || 0 }}</td>
                    <td>{{ t.approval_mode }}</td>
                  </tr>
                  <tr v-if="!formTemplates.length">
                    <td colspan="4" class="hr-text-center hr-text-muted">No onboarding form templates yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="hr-mt-4">
              <router-link to="/employee-lifecycle" class="hr-btn hr-btn-secondary">
                <i class="bx bx-timeline"></i> Open Employee Lifecycle hub
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Vacancy modal -->
    <ModalWrapper ref="vacancyModal" title="Create Vacancy" size="large">
      <template #body>
        <form class="hr-form-grid" @submit.prevent="submitVacancy">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Vacancy title *</label>
            <input v-model="vacancyForm.title" class="hr-input" type="text" required>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Position title *</label>
            <input v-model="vacancyForm.positionTitle" class="hr-input" type="text" required>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Department</label>
            <select v-model="vacancyForm.departmentId" class="hr-select">
              <option :value="0">— Select —</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Employment type</label>
            <select v-model="vacancyForm.employmentType" class="hr-select">
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
              <option>Internship</option>
              <option>Temporary</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Closing date</label>
            <input v-model="vacancyForm.closingDate" class="hr-input" type="date">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Vacancies count</label>
            <input v-model.number="vacancyForm.vacanciesCount" class="hr-input" type="number" min="1">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Salary range</label>
            <input v-model="vacancyForm.salaryRange" class="hr-input" type="text" placeholder="e.g. KES 80,000 - 120,000">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Location</label>
            <input v-model="vacancyForm.location" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea v-model="vacancyForm.description" class="hr-textarea" rows="3"></textarea>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Requirements</label>
            <textarea v-model="vacancyForm.requirements" class="hr-textarea" rows="3"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Status</label>
            <select v-model="vacancyForm.status" class="hr-select">
              <option>Draft</option>
              <option>Open</option>
            </select>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.vacancyModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="submitVacancy">Save</button>
      </template>
    </ModalWrapper>

    <!-- Application modal -->
    <ModalWrapper ref="applicationModal" title="Add Application" size="medium">
      <template #body>
        <form class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Vacancy *</label>
            <select v-model="applicationForm.vacancyId" class="hr-select" required>
              <option :value="0">— Select open vacancy —</option>
              <option v-for="v in openVacancies" :key="v.id" :value="v.id">{{ v.title }} — {{ v.position_title }}</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Full name *</label>
            <input v-model="applicationForm.applicantName" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Email *</label>
            <input v-model="applicationForm.email" class="hr-input" type="email">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Phone</label>
            <input v-model="applicationForm.phone" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">National ID</label>
            <input v-model="applicationForm.nationalId" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Cover letter</label>
            <textarea v-model="applicationForm.coverLetter" class="hr-textarea" rows="3"></textarea>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Resume (PDF/DOC)</label>
            <input ref="resumeInput" type="file" class="hr-input" accept=".pdf,.doc,.docx">
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.applicationModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="submitApplication">Submit</button>
      </template>
    </ModalWrapper>

    <!-- Interview modal -->
    <ModalWrapper ref="interviewModal" title="Schedule Interview" size="medium">
      <template #body>
        <form class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Candidate *</label>
            <select v-model="interviewForm.applicationId" class="hr-select">
              <option :value="0">— Select —</option>
              <option v-for="a in shortlistPool" :key="a.id" :value="a.id">{{ a.applicant_name }} — {{ a.position_title }}</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Date & time *</label>
            <input v-model="interviewForm.scheduledAt" class="hr-input" type="datetime-local">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Type</label>
            <select v-model="interviewForm.interviewType" class="hr-select">
              <option>In-Person</option>
              <option>Video</option>
              <option>Phone</option>
            </select>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Location / link</label>
            <input v-model="interviewForm.locationOrLink" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Interviewer</label>
            <input v-model="interviewForm.interviewerName" class="hr-input" type="text">
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.interviewModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="submitInterview">Schedule</button>
      </template>
    </ModalWrapper>

    <!-- Offer modal -->
    <ModalWrapper ref="offerModal" title="Generate Offer Letter" size="medium">
      <template #body>
        <form class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Selected candidate *</label>
            <select v-model="offerForm.applicationId" class="hr-select">
              <option :value="0">— Select —</option>
              <option v-for="a in selectedPool" :key="a.id" :value="a.id">{{ a.applicant_name }} — {{ a.position_title }}</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Salary offered</label>
            <input v-model="offerForm.salaryOffered" class="hr-input" type="text">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Start date</label>
            <input v-model="offerForm.startDate" class="hr-input" type="date">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Benefits</label>
            <textarea v-model="offerForm.benefits" class="hr-textarea" rows="2"></textarea>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Terms</label>
            <textarea v-model="offerForm.terms" class="hr-textarea" rows="2"></textarea>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.offerModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="submitOffer">Generate</button>
      </template>
    </ModalWrapper>

    <!-- Offer preview -->
    <ModalWrapper ref="offerPreviewModal" title="Offer Letter Preview" size="large">
      <template #body>
        <div class="offer-preview" v-html="offerPreviewHtml"></div>
      </template>
    </ModalWrapper>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, getRight, toast } from "@/api";

export default {
  name: "RecruitmentManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      activeTab: "vacancies",
      metrics: {
        openVacancies: 0,
        totalApplications: 0,
        shortlisted: 0,
        inInterview: 0,
        selected: 0,
        offered: 0
      },
      vacancies: [],
      applications: [],
      interviews: [],
      offers: [],
      departments: [],
      loadingVacancies: false,
      loadingApplications: false,
      loadingInterviews: false,
      loadingOffers: false,
      submitting: false,
      vacancyStatusFilter: "",
      applicationVacancyFilter: 0,
      applicationStatusFilter: "",
      vacancyForm: {
        title: "",
        positionTitle: "",
        departmentId: 0,
        description: "",
        requirements: "",
        vacanciesCount: 1,
        salaryRange: "",
        employmentType: "Full-time",
        location: "",
        closingDate: "",
        status: "Draft"
      },
      applicationForm: {
        vacancyId: 0,
        applicantName: "",
        email: "",
        phone: "",
        nationalId: "",
        coverLetter: ""
      },
      interviewForm: {
        applicationId: 0,
        scheduledAt: "",
        interviewType: "In-Person",
        locationOrLink: "",
        interviewerName: ""
      },
      offerForm: {
        applicationId: 0,
        salaryOffered: "",
        startDate: "",
        benefits: "",
        terms: ""
      },
      offerPreviewHtml: "",
      suggestVacancyId: 0,
      suggestedThreshold: 60,
      suggestedApplications: [],
      loadingSuggested: false,
      onboardingSubmissions: [],
      onboardingStatusFilter: "Submitted",
      loadingOnboarding: false,
      automationSettings: {
        recruitment_auto_score_on_apply: "1",
        recruitment_shortlist_threshold: "60",
        recruitment_auto_email: "1"
      },
      empNumberSettings: { mode: "auto", prefix: "EMP", pad_length: 5 },
      previewEmpNo: "",
      formTemplates: [],
      positions: [],
      formTemplateUpload: { name: "", approvalMode: "sequential", approverPositionId: 0 }
    };
  },
  computed: {
    canVacancies() {
      return getRight("RECRUITMENT VACANCIES") || getRight("VACANCY MANAGEMENT") || getRight("RECRUITMENT");
    },
    canApplications() {
      return getRight("RECRUITMENT APPLICATIONS") || getRight("RECRUITMENT");
    },
    canShortlist() {
      return getRight("RECRUITMENT SHORTLISTING") || getRight("SHORTLISTING MANAGEMENT") || getRight("RECRUITMENT");
    },
    canReject() {
      return this.canShortlist || this.canApplications;
    },
    canInterviews() {
      return getRight("RECRUITMENT INTERVIEWS") || getRight("INTERVIEW MANAGEMENT") || getRight("RECRUITMENT");
    },
    canSelection() {
      return getRight("RECRUITMENT SELECTION") || getRight("APPOINTMENT MANAGEMENT") || getRight("RECRUITMENT");
    },
    canOffers() {
      return getRight("RECRUITMENT OFFERS") || getRight("RECRUITMENT");
    },
    visibleTabs() {
      const all = [
        { id: "vacancies", label: "Vacancies", icon: "bx bx-briefcase", show: this.canVacancies },
        { id: "applications", label: "Applications", icon: "bx bx-file", show: this.canApplications },
        { id: "shortlisting", label: "Shortlisting", icon: "bx bx-list-check", show: this.canShortlist },
        { id: "interviews", label: "Interviews", icon: "bx bx-calendar", show: this.canInterviews },
        { id: "selection", label: "Selection", icon: "bx bx-check-circle", show: this.canSelection },
        { id: "offers", label: "Offer Letters", icon: "bx bx-envelope", show: this.canOffers },
        { id: "smartShortlist", label: "Smart Shortlist", icon: "bx bx-brain", show: this.canShortlist },
        { id: "onboarding", label: "Onboarding", icon: "bx bx-user-check", show: this.canOffers || this.canSelection },
        { id: "settings", label: "Automation & Forms", icon: "bx bx-cog", show: this.canOffers || this.canVacancies }
      ];
      return all.filter((t) => t.show);
    },
    onboardingCandidates() {
      return this.applications.filter((a) => ["Selected", "Offered"].includes(a.status));
    },
    openVacancies() {
      return this.vacancies.filter((v) => v.status === "Open");
    },
    applicationsForShortlist() {
      return this.applications.filter((a) => a.status === "Applied");
    },
    applicationsForSelection() {
      return this.applications.filter((a) => ["Shortlisted", "Interview"].includes(a.status));
    },
    shortlistPool() {
      return this.applications.filter((a) => ["Shortlisted", "Interview", "Applied"].includes(a.status));
    },
    selectedPool() {
      return this.applications.filter((a) => a.status === "Selected");
    }
  },
  mounted() {
    if (this.visibleTabs.length) {
      this.activeTab = this.visibleTabs[0].id;
    }
    this.loadDepartments();
    this.loadPositions();
    this.loadAutomationSettings();
    this.loadEmpNumberSettings();
    this.loadFormTemplates();
    this.refreshAll();
  },
  methods: {
    sessionFields(data) {
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      data.append("sessionNames", sessionStorage.getItem("names") || "");
      return data;
    },
    formatDate(v) {
      if (!v) return "—";
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d.toLocaleDateString();
    },
    formatDateTime(v) {
      if (!v) return "—";
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d.toLocaleString();
    },
    vacancyBadge(s) {
      if (s === "Open") return "hr-badge-success";
      if (s === "Closed") return "hr-badge-warning";
      if (s === "Filled") return "hr-badge-primary";
      return "hr-badge-secondary";
    },
    appBadge(s) {
      const m = {
        Applied: "hr-badge-secondary",
        Shortlisted: "hr-badge-info",
        Interview: "hr-badge-warning",
        Selected: "hr-badge-primary",
        Offered: "hr-badge-success",
        Rejected: "hr-badge-danger"
      };
      return m[s] || "hr-badge-secondary";
    },
    loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data?.departments) {
          this.departments = res.data.data.departments;
        }
      }).catch(() => {});
    },
    loadMetrics() {
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentMetrics");
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data) {
          this.metrics = { ...this.metrics, ...res.data.data };
        }
      }).catch(() => {});
    },
    loadVacancies() {
      this.loadingVacancies = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentVacancies");
      if (this.vacancyStatusFilter) data.append("status", this.vacancyStatusFilter);
      execute(data, authUrl).then((res) => {
        this.loadingVacancies = false;
        this.vacancies = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {
        this.loadingVacancies = false;
        this.vacancies = [];
      });
    },
    loadApplications() {
      this.loadingApplications = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentApplications");
      if (this.applicationVacancyFilter) data.append("vacancyId", this.applicationVacancyFilter);
      if (this.applicationStatusFilter) data.append("status", this.applicationStatusFilter);
      execute(data, authUrl).then((res) => {
        this.loadingApplications = false;
        this.applications = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {
        this.loadingApplications = false;
        this.applications = [];
      });
    },
    loadInterviews() {
      this.loadingInterviews = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentInterviews");
      execute(data, authUrl).then((res) => {
        this.loadingInterviews = false;
        this.interviews = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {
        this.loadingInterviews = false;
        this.interviews = [];
      });
    },
    loadOffers() {
      this.loadingOffers = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentOfferLetters");
      execute(data, authUrl).then((res) => {
        this.loadingOffers = false;
        this.offers = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {
        this.loadingOffers = false;
        this.offers = [];
      });
    },
    refreshAll() {
      this.loadMetrics();
      this.loadVacancies();
      this.loadApplications();
      this.loadInterviews();
      this.loadOffers();
    },
    openVacancyModal() {
      this.vacancyForm = {
        title: "",
        positionTitle: "",
        departmentId: 0,
        description: "",
        requirements: "",
        vacanciesCount: 1,
        salaryRange: "",
        employmentType: "Full-time",
        location: "",
        closingDate: "",
        status: "Draft"
      };
      this.$refs.vacancyModal?.open();
    },
    submitVacancy() {
      if (!this.vacancyForm.title.trim() || !this.vacancyForm.positionTitle.trim()) {
        toast.warning("Title and position are required.");
        return;
      }
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "createRecruitmentVacancy");
      Object.keys(this.vacancyForm).forEach((k) => data.append(k, this.vacancyForm[k]));
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) {
          toast.success(res.data.message || "Vacancy created.");
          this.$refs.vacancyModal?.close();
          this.refreshAll();
        } else {
          toast.error(res.data.message || "Failed.");
        }
      }).catch(() => {
        this.submitting = false;
        toast.error("Request failed.");
      });
    },
    publishVacancy(v) {
      const data = this.sessionFields(new FormData());
      data.append("function", "updateRecruitmentVacancy");
      data.append("vacancyId", v.id);
      data.append("status", "Open");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Vacancy published.");
          this.loadVacancies();
          this.loadMetrics();
        } else toast.error(res.data.message);
      });
    },
    filterByVacancy(v) {
      this.applicationVacancyFilter = v.id;
      this.activeTab = "applications";
      this.loadApplications();
    },
    openApplicationModal() {
      this.applicationForm = { vacancyId: 0, applicantName: "", email: "", phone: "", nationalId: "", coverLetter: "" };
      this.$refs.applicationModal?.open();
    },
    submitApplication() {
      if (!this.applicationForm.vacancyId || !this.applicationForm.applicantName || !this.applicationForm.email) {
        toast.warning("Vacancy, name and email are required.");
        return;
      }
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "createRecruitmentApplication");
      data.append("vacancyId", this.applicationForm.vacancyId);
      data.append("applicantName", this.applicationForm.applicantName);
      data.append("email", this.applicationForm.email);
      data.append("phone", this.applicationForm.phone);
      data.append("nationalId", this.applicationForm.nationalId);
      data.append("coverLetter", this.applicationForm.coverLetter);
      const file = this.$refs.resumeInput?.files?.[0];
      if (file) data.append("resume", file);
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) {
          toast.success("Application saved.");
          this.$refs.applicationModal?.close();
          this.refreshAll();
        } else toast.error(res.data.message);
      }).catch(() => {
        this.submitting = false;
        toast.error("Request failed.");
      });
    },
    shortlistApplicant(a) {
      const note = window.prompt("Optional message for the candidate email (interview details, etc.):", "");
      if (note === null) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "shortlistRecruitmentApplicant");
      data.append("applicationId", a.id);
      data.append("interviewNote", note);
      data.append("sendEmail", "1");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success(res.data.message || "Shortlisted.");
          this.refreshAll();
        } else toast.error(res.data.message);
      });
    },
    rejectApplicant(a) {
      const reason = window.prompt("Rejection reason (optional):", "");
      if (reason === null) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "rejectRecruitmentApplicant");
      data.append("applicationId", a.id);
      data.append("rejectionReason", reason);
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Application rejected.");
          this.refreshAll();
        } else toast.error(res.data.message);
      });
    },
    openInterviewModal() {
      this.interviewForm = { applicationId: 0, scheduledAt: "", interviewType: "In-Person", locationOrLink: "", interviewerName: "" };
      this.$refs.interviewModal?.open();
    },
    submitInterview() {
      if (!this.interviewForm.applicationId || !this.interviewForm.scheduledAt) {
        toast.warning("Candidate and schedule are required.");
        return;
      }
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "scheduleRecruitmentInterview");
      data.append("applicationId", this.interviewForm.applicationId);
      data.append("scheduledAt", this.interviewForm.scheduledAt.replace("T", " ") + ":00");
      data.append("interviewType", this.interviewForm.interviewType);
      data.append("locationOrLink", this.interviewForm.locationOrLink);
      data.append("interviewerName", this.interviewForm.interviewerName);
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) {
          toast.success("Interview scheduled.");
          this.$refs.interviewModal?.close();
          this.refreshAll();
        } else toast.error(res.data.message);
      }).catch(() => {
        this.submitting = false;
      });
    },
    completeInterview(i) {
      const feedback = window.prompt("Interview feedback:", "") || "";
      const data = this.sessionFields(new FormData());
      data.append("function", "updateRecruitmentInterview");
      data.append("interviewId", i.id);
      data.append("status", "Completed");
      data.append("feedback", feedback);
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Interview marked complete.");
          this.loadInterviews();
        }
      });
    },
    selectApplicant(a) {
      if (!window.confirm(`Select ${a.applicant_name} for this role?`)) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "selectRecruitmentApplicant");
      data.append("applicationId", a.id);
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Candidate selected.");
          this.refreshAll();
        } else toast.error(res.data.message);
      });
    },
    openOfferModal() {
      this.offerForm = { applicationId: 0, salaryOffered: "", startDate: "", benefits: "", terms: "" };
      this.$refs.offerModal?.open();
    },
    submitOffer() {
      if (!this.offerForm.applicationId) {
        toast.warning("Select a candidate.");
        return;
      }
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "generateRecruitmentOfferLetter");
      Object.keys(this.offerForm).forEach((k) => data.append(k, this.offerForm[k]));
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) {
          toast.success("Offer generated.");
          this.$refs.offerModal?.close();
          this.activeTab = "offers";
          this.loadOffers();
          this.loadApplications();
        } else toast.error(res.data.message);
      }).catch(() => {
        this.submitting = false;
      });
    },
    sendOffer(o) {
      if (!window.confirm(`Send offer ${o.offer_ref} to ${o.applicant_name} by email?`)) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "sendRecruitmentOfferLetter");
      data.append("offerId", o.id);
      data.append("sendEmail", "1");
      data.append("sendOnboardingInvite", "1");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          const msg = res.data.message || "Offer sent.";
          const inv = res.data.data?.onboardingInvite;
          if (inv && !inv.error) {
            toast.success(msg + " Onboarding invite sent.");
          } else {
            toast.success(msg);
          }
          this.refreshAll();
        } else toast.error(res.data.message);
      });
    },
    previewOffer(o) {
      this.offerPreviewHtml = o.offer_html || "<p>No preview available.</p>";
      this.$refs.offerPreviewModal?.open();
    },
    scoreClass(score) {
      const n = parseFloat(score);
      if (n >= 70) return "text-success";
      if (n >= 50) return "text-warning";
      return "text-muted";
    },
    formatScore(score) {
      if (score === null || score === undefined || score === "") return "—";
      return Math.round(parseFloat(score)) + "%";
    },
    loadPositions() {
      const data = new FormData();
      data.append("function", "getPositions");
      execute(data, authUrl).then((res) => {
        const list = res.data.data?.positions || res.data.data || [];
        this.positions = Array.isArray(list) ? list : [];
      }).catch(() => {});
    },
    loadSuggestedShortlist() {
      if (!this.suggestVacancyId) {
        this.suggestedApplications = [];
        return;
      }
      this.loadingSuggested = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentSuggestedShortlist");
      data.append("vacancyId", this.suggestVacancyId);
      data.append("threshold", this.suggestedThreshold);
      execute(data, authUrl).then((res) => {
        this.loadingSuggested = false;
        const payload = res.data.data || {};
        this.suggestedApplications = Array.isArray(payload.applications) ? payload.applications : [];
        if (payload.threshold != null) {
          this.suggestedThreshold = payload.threshold;
        }
      }).catch(() => {
        this.loadingSuggested = false;
        this.suggestedApplications = [];
      });
    },
    scoreVacancyApplications() {
      if (!this.suggestVacancyId) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "scoreRecruitmentApplications");
      data.append("vacancyId", this.suggestVacancyId);
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Applications scored.");
          this.loadSuggestedShortlist();
        }
      });
    },
    bulkShortlistSuggested() {
      if (!this.suggestVacancyId) return;
      if (!window.confirm("Shortlist all suggested applicants and send email notifications?")) return;
      const data = this.sessionFields(new FormData());
      data.append("function", "bulkShortlistRecruitmentSuggested");
      data.append("vacancyId", this.suggestVacancyId);
      data.append("threshold", this.suggestedThreshold);
      data.append("sendEmail", "1");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success(res.data.message || "Bulk shortlist complete.");
          this.refreshAll();
          this.loadSuggestedShortlist();
        } else toast.error(res.data.message);
      });
    },
    sendOnboardingInvite(a) {
      const data = this.sessionFields(new FormData());
      data.append("function", "initiateRecruitmentOnboarding");
      data.append("applicationId", a.id);
      data.append("sendEmail", "1");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success(res.data.message || "Onboarding invite sent.");
        } else toast.error(res.data.message);
      });
    },
    hireApplicant(a) {
      let empNo = "";
      if (this.empNumberSettings.mode === "manual") {
        empNo = window.prompt("Enter employee number for " + a.applicant_name + ":", "") || "";
        if (!empNo.trim()) return;
      }
      const data = this.sessionFields(new FormData());
      data.append("function", "hireRecruitmentApplicant");
      data.append("applicationId", a.id);
      if (empNo) data.append("empNo", empNo.trim());
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          const d = res.data.data || {};
          let msg = "Employee created" + (d.empNo ? ": " + d.empNo : ".");
          if (d.userAccountCreated && d.temporaryPassword) {
            msg += " Login created — temp password sent by email.";
          } else if (d.userAccountCreated) {
            msg += " System login linked.";
          }
          toast.success(msg);
          this.refreshAll();
          if (d.employeeId && window.confirm("Open employee profile and lifecycle timeline?")) {
            sessionStorage.setItem("employeeId", d.employeeId);
            this.$router.push({ path: "/employee-details-view", query: { tab: "lifecycle" } });
          }
        } else toast.error(res.data.message);
      });
    },
    loadOnboardingSubmissions() {
      this.loadingOnboarding = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "getOnboardingSubmissionsForReview");
      data.append("status", this.onboardingStatusFilter);
      execute(data, authUrl).then((res) => {
        this.loadingOnboarding = false;
        this.onboardingSubmissions = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {
        this.loadingOnboarding = false;
        this.onboardingSubmissions = [];
      });
    },
    approveSubmission(s) {
      const data = this.sessionFields(new FormData());
      data.append("function", "approveHrFormSubmission");
      data.append("submissionId", s.id);
      data.append("action", "approve");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          toast.success("Approved.");
          this.loadOnboardingSubmissions();
        } else toast.error(res.data.message);
      });
    },
    downloadFormsPack(s) {
      const inviteId = s.invite_id || s.onboarding_invite_id;
      if (!inviteId) {
        toast.warning("No invite linked.");
        return;
      }
      const data = this.sessionFields(new FormData());
      data.append("function", "downloadOnboardingFormsPack");
      data.append("inviteId", inviteId);
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data?.submissions) {
          const blob = new Blob([JSON.stringify(res.data.data.submissions, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "onboarding-forms-" + inviteId + ".json";
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    },
    loadAutomationSettings() {
      const data = this.sessionFields(new FormData());
      data.append("function", "getRecruitmentAutomationSettings");
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data) {
          this.automationSettings = { ...this.automationSettings, ...res.data.data };
          if (res.data.data.recruitment_shortlist_threshold) {
            this.suggestedThreshold = parseFloat(res.data.data.recruitment_shortlist_threshold) || 60;
          }
        }
      }).catch(() => {});
    },
    saveAutomationSettings() {
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "saveRecruitmentAutomationSettings");
      Object.keys(this.automationSettings).forEach((k) => data.append(k, this.automationSettings[k]));
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) toast.success("Automation settings saved.");
        else toast.error(res.data.message);
      }).catch(() => { this.submitting = false; });
    },
    loadEmpNumberSettings() {
      const data = this.sessionFields(new FormData());
      data.append("function", "getHrEmployeeNumberSettings");
      execute(data, authUrl).then((res) => {
        if (res.data.success && res.data.data) {
          this.empNumberSettings = { ...this.empNumberSettings, ...res.data.data };
        }
      }).catch(() => {});
    },
    saveEmpNumberSettings() {
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "saveHrEmployeeNumberSettings");
      data.append("mode", this.empNumberSettings.mode);
      data.append("prefix", this.empNumberSettings.prefix || "EMP");
      data.append("pad_length", this.empNumberSettings.pad_length || 5);
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) toast.success("Employee number settings saved.");
        else toast.error(res.data.message);
      }).catch(() => { this.submitting = false; });
    },
    previewNextEmpNo() {
      const data = this.sessionFields(new FormData());
      data.append("function", "generateNextEmployeeNumber");
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.previewEmpNo = res.data.data?.empNo || "";
        }
      });
    },
    loadFormTemplates() {
      const data = this.sessionFields(new FormData());
      data.append("function", "getHrFormTemplates");
      execute(data, authUrl).then((res) => {
        this.formTemplates = res.data.success && Array.isArray(res.data.data) ? res.data.data : [];
      }).catch(() => {});
    },
    uploadFormTemplate() {
      const file = this.$refs.formFileInput?.files?.[0];
      if (!file) {
        toast.warning("Choose a form file to upload.");
        return;
      }
      this.submitting = true;
      const data = this.sessionFields(new FormData());
      data.append("function", "uploadHrFormTemplateFile");
      data.append("formFile", file);
      data.append("templateName", this.formTemplateUpload.name || file.name);
      if (this.formTemplateUpload.approverPositionId) {
        data.append("approvers", JSON.stringify([{
          position_id: this.formTemplateUpload.approverPositionId,
          approval_order: 1
        }]));
      }
      data.append("approvalMode", this.formTemplateUpload.approvalMode);
      execute(data, authUrl).then((res) => {
        this.submitting = false;
        if (res.data.success) {
          toast.success("Form template created.");
          this.formTemplateUpload.name = "";
          if (this.$refs.formFileInput) this.$refs.formFileInput.value = "";
          this.loadFormTemplates();
        } else toast.error(res.data.message);
      }).catch(() => { this.submitting = false; });
    }
  },
  watch: {
    activeTab(tab) {
      if (tab === "smartShortlist" && this.suggestVacancyId) {
        this.loadSuggestedShortlist();
      }
      if (tab === "onboarding") {
        this.loadOnboardingSubmissions();
      }
      if (tab === "settings") {
        this.loadFormTemplates();
      }
    }
  }
};
</script>

<style scoped>
.recruitment-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border-bottom: 1px solid var(--hr-border, #e5e7eb);
  padding-bottom: 8px;
}
.recruitment-tab {
  padding: 8px 16px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #64748b;
}
.recruitment-tab.active {
  background: #3f51b5;
  color: #fff;
  border-color: #3f51b5;
}
.recruitment-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}
.recruitment-filters .hr-select {
  min-width: 180px;
}
.offer-preview {
  max-height: 60vh;
  overflow: auto;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}
.text-success { color: #16a34a; }
.text-warning { color: #d97706; }
.text-muted { color: #64748b; }
</style>
